import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ExclamationCircleFilled,
  MoreOutlined,
  SendOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { getPlanInfo } from '@mm/state/helpers/billing-info-helpers';
import { SubscriptionTabDefinition } from '@mm/state/helpers/billing-subscription-tab-helpers';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { EnhancedPlan } from '@mm/state/interfaces/subscription';
import PromotionModel from '@mm/state/model/PromotionModel';
import {
  isBilling2023Subscription,
  isBilling2025Subscription,
} from '@mm/state/model/SubscriptionModel';
import {
  companyAdminSelector,
  twilioNumbersSelector,
} from '@mm/state/selectors/viewSelectors';
import { Skeleton } from 'antd';
import noImage from 'assets/icons/contact_noimage.svg';
import classNames from 'classnames';
import { isNumber } from 'lodash';
import moment from 'moment/moment';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { ImageWithFallback } from '../../../../../../../common/components/ImageWithFallback/ImageWithFallback';
import { PlanBadge } from '../../../../../../../common/components/ProBadge';
import { SettingsRoutes } from '../../../../../../../routes/settings-routes';
import { ManageSubsctiptionProps } from '../../../ManageSubsctiptionPanel/ManageSubsctiptionPanel';
import { getBillingPlanForTab } from '../helpers';
import './BillingPageCard.pcss';
import { BillingPageMetadataCollapser } from './BillingPageMetadata/BillingPageMetadataCollapser';
import { BillingPageTable } from './BillingPageTable/BillingPageTable';
import { BillingUserLimit } from './BillingUserLimit/BillingUserLimit';
import PromoRedemptionSelector from './PromoRedemptionSelector/PromoRedemptionSelector';
import { useBillingDetails } from './use-billing-details';
import { Menu } from '../../../../../../../editor/features/beta/modules/Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/NodeMenu/NodeMenu';
import { Button } from '@mm/components';
import { selectContactsGatheredAnalytics } from '@mm/state/selectors/botAnalyticsSelector';
import { useModalActions } from '@mm/state/modules/modalsState/actions';

type Props = {
  page: EnhancedBillingPage;
  openRemoveFromTeamModal: () => void;
  promotions: PromotionModel[];
  planTab: SubscriptionTabDefinition;
  loadPricesForNextLevel: (subscriptionId: number, level: number) => void;
};

// tslint:disable-next-line:cyclomatic-complexity
const BillingPageCard = (props: Props) => {
  const {
    page,
    promotions,
    openRemoveFromTeamModal,
    planTab,
    loadPricesForNextLevel,
  } = props;
  const { outgoingMessagesLimit, downgradePlansIds } = planTab;
  const ref = useRef<HTMLDivElement>(null);
  const billingPlan = getBillingPlanForTab(planTab);
  const { getCurrentSubscription, getSubscription } =
    useSubscriptionCRUDActions();
  const { openAddPromocodeModal } = useModalActions();
  const history = useHistory();
  const isCompanyAdmin = useSelector(companyAdminSelector);
  const twilioNumbers = useSelector(twilioNumbersSelector);

  const { contactsGathered } =
    useSelector(selectContactsGatheredAnalytics) || {};
  const contactsGatheredPerPage = contactsGathered?.[page.id];

  const isExpiredPromocode =
    moment().diff(moment(page.redemption?.expiresAt)) > 0;
  const redeemedPromocode =
    page.redemption?.freeLeads && !isExpiredPromocode
      ? page.redemption.code
      : '';

  const planInfoMemoised = useMemo(
    () => getPlanInfo(page, billingPlan),
    [page, billingPlan],
  );

  const plan: EnhancedPlan[] = [];

  if (planTab.plan) {
    plan.push(planTab.plan);
  }

  const isFreePage = !page.subscriptions?.length;
  const isTeamOrFreePlan = planInfoMemoised.isAnyTeamPlan || isFreePage;

  const [busy, setBusy] = useState(false);

  const showPromoRedeemSelector =
    !!planInfoMemoised.isFreePlan && !!promotions.length && isCompanyAdmin;

  const { currentSubscription, subscriptionIsCanceled } = useBillingDetails(
    page,
    planInfoMemoised,
    planTab.teamSubscription,
  );

  const isBilling2023Plan =
    page.subscription && isBilling2023Subscription(page.subscription);

  const isBilling2025Plan =
    page.subscription && isBilling2025Subscription(page.subscription);

  const canManagePlan =
    isFreePage ||
    (!isFreePage &&
      !planInfoMemoised.isAnyTeamPlan &&
      !subscriptionIsCanceled &&
      isCompanyAdmin);

  const [currentSubscriptionId, setCurrentSubscriptionId] = useState(0);

  useEffect(() => {
    if (
      page.subscription &&
      !currentSubscription &&
      !planInfoMemoised.isAnyTeamPlan &&
      !busy &&
      !isBilling2023Plan
    ) {
      if (page.subscription.id === currentSubscriptionId) {
        return;
      }

      getCurrentSubscription(page.id, false);
      setCurrentSubscriptionId(page.subscription.id);
    }
  }, [
    currentSubscriptionId,
    isBilling2023Plan,
    currentSubscription,
    getCurrentSubscription,
    page.id,
    busy,
    page.subscription,
    planInfoMemoised.isAnyTeamPlan,
  ]);

  const retrieveSubscription = async () => {
    if (page.subscription && isBilling2023Plan) {
      setBusy(true);
      await getSubscription(page.subscription?.id, page.id);
      setBusy(false);
    }
  };

  useEffect(() => {
    retrieveSubscription();
  }, []);

  const customTeamPlanConditions =
    (planInfoMemoised.isLegacyTeamPlan || planInfoMemoised.isCustomTeam) &&
    !!twilioNumbers.length;

  const showSendCreditsUsed =
    !isBilling2023Plan &&
    ((planInfoMemoised.isAnyTeamPlan && outgoingMessagesLimit) ||
      customTeamPlanConditions);

  const data: ManageSubsctiptionProps = {
    accountName: page.name ?? '',
    downgradePlansIds,
  };

  const onManagePlanClick = () => {
    history.push(
      SettingsRoutes.billing.manageSubscrptions.getLink(
        planTab.planCode,
        page.id,
      ),
      { ...data },
    );
  };

  return (
    <div
      className={classNames('billing-page-card', {
        'billing-page-card-team': planInfoMemoised.isAnyTeamPlan,
        'billing-page-card-team-trial': planInfoMemoised.isTrialTeam,
      })}
      ref={ref}
    >
      {page.xrayLockedAt && (
        <div className="billing-page-card__limit-alert">
          <ExclamationCircleFilled className="billing-page-card__exclamation-circule" />
          X-Ray Pixel Capture Limit is reached
        </div>
      )}
      <div
        className={classNames('billing-page-body', {
          'billing-page-body-non-team': !planInfoMemoised.isAnyTeamPlan,
        })}
      >
        <div className="billing-page-card__header">
          <div className="billing-page-card__header-with-avatar">
            <ImageWithFallback
              src={page.squarePicture || noImage}
              fallbackImage={noImage}
              className="billing-page-card__page-image"
              alt=""
            />

            <div className="billing-page-card__header-page-details">
              <div className="billing-page-card__meta-data-top">
                <div className="billing-page-card__name">{page.name}</div>

                <PlanBadge
                  billingType={billingPlan || page.pageBillingType}
                  facebookPage={page}
                />
              </div>

              <div className="billing-page-card__connected-since">
                Created: {moment(page.createdAt).format('MM/DD/YYYY')}
              </div>

              {planInfoMemoised.isAnyTeamPlan && (
                <div>
                  <FacebookPageIdLink
                    remoteId={page.remoteId}
                    isRealPage={page.realFacebookPage}
                    className={classNames(
                      'billing-page-card__metadata-page-id-team-plan',
                      {
                        'billing-page-card__metadata-page-id-trial':
                          planInfoMemoised.isTrialTeam,
                      },
                    )}
                  />

                  {showSendCreditsUsed && (
                    <div className="billing-page-card__team-sends">
                      <div className="billing-page-card__team-sends-text">
                        <SendOutlined />
                        Credits used
                      </div>
                      <div className="billing-page-card__team-sends-number">
                        {customTeamPlanConditions
                          ? page?.currentOutgoingMessagesStatistic?.count
                          : page.messagesStatistic.pageSpecificCount}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="billing-page-card__header-actions">
            {showPromoRedeemSelector && (
              <PromoRedemptionSelector
                facebookPageId={page.id}
                promotions={promotions}
              />
            )}

            {redeemedPromocode && (
              <PrimaryButton
                className={classNames(
                  'billing-page-card__add-promocode-button',
                  {
                    'billing-page-card__promocode-redeemed': redeemedPromocode,
                  },
                )}
                color="transparent"
                onClick={() =>
                  openAddPromocodeModal(
                    page.id,
                    page.companyId,
                    redeemedPromocode,
                  )
                }
              >
                Discount applied: {redeemedPromocode}
              </PrimaryButton>
            )}

            {canManagePlan && (
              <PrimaryButton
                className="billing-page-card__manage-plan-button"
                color="secondary-blue"
                onClick={onManagePlanClick}
              >
                Manage Plan
              </PrimaryButton>
            )}
          </div>
        </div>

        {!planInfoMemoised.isAnyTeamPlan && !busy && (
          <div className="billing-page-card__table">
            <BillingPageTable
              page={page}
              planTab={planTab}
              planInfoMemoised={planInfoMemoised}
              loadPricesForNextLevel={loadPricesForNextLevel}
              onUpgradeClick={onManagePlanClick}
            />
          </div>
        )}

        {busy && <Skeleton active />}
        <div className="billing-card-right-section">
          {planInfoMemoised.isAnyTeamPlan && contactsGatheredPerPage && (
            <div className="billing-card-right-section__leads-count">
              {' '}
              <TeamOutlined className="billing-card-right-section__leads-icon" />
              {contactsGatheredPerPage}
            </div>
          )}
          <div className="billing-card-right-section__actions">
            {planInfoMemoised.isAnyTeamPlan &&
              plan &&
              isNumber(planTab.plan?.pagesLimit) && (
                <Menu
                  className="billing-card-right-section__menu"
                  menu={[
                    {
                      children: (
                        <div className="billing-card-right-section__menu-action">
                          Delete
                        </div>
                      ),
                      onClick: openRemoveFromTeamModal,
                    },
                    {
                      children: (
                        <div className="billing-card-right-section__menu-action">
                          {redeemedPromocode
                            ? 'View Promocode'
                            : 'Add Promocode'}
                        </div>
                      ),
                      onClick: () =>
                        openAddPromocodeModal(
                          page.id,
                          page.companyId,
                          redeemedPromocode,
                        ),
                    },
                  ]}
                  direction="bottom"
                  useThreeDotButton
                />
              )}

            <div className="billing-page-card__metadata-wrapper">
              {isFreePage &&
                !planInfoMemoised.isAnyTeamPlan &&
                page.realFacebookPage && (
                  <FacebookPageIdLink
                    remoteId={page.remoteId}
                    isRealPage={page.realFacebookPage}
                  />
                )}

              {isBilling2023Plan &&
                !isBilling2025Plan &&
                currentSubscription && (
                  <BillingUserLimit
                    limitType={
                      planInfoMemoised.isAnyTeamPlan ? 'page' : 'subscription'
                    }
                    subscription={currentSubscription}
                    facebookPageId={page.id}
                    retrieveSubscription={retrieveSubscription}
                  />
                )}

              {!isTeamOrFreePlan && (
                <BillingPageMetadataCollapser page={page} planTab={planTab} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPageCard;

const FacebookPageIdLink = ({
  remoteId,
  className,
  isRealPage,
}: {
  remoteId: string;
  className?: string;
  isRealPage?: boolean;
}) => {
  if (!isRealPage) return <></>;

  return (
    <div
      className={classNames('billing-page-card__metadata-page-id', className)}
    >
      Facebook Page Id:{' '}
      <a
        className="billing-page-card__facebook-link-grey"
        href={`https://facebook.com/${remoteId}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {remoteId}
      </a>
    </div>
  );
};
